import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import addLogDocument from "../addLogDocument"; // Custom log function
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import { Box } from "@mui/material";
import Spinner from "../../loading/Spinner.js";
import Icon1 from "../../icons/Icon.js";
import SmallSpinner from "../../loading/SmallSpinner.js";
import { getDocDataByRef, getFlutterTime } from "../../models/DataFunctions.js";
import { translateDocumentId } from "../../pages/folder_pages/FolderV2.js";
import createDocApi from "../database/createDocApi.js";
import { addActivityLog } from "../../config/OnlineStatus.js";

const UploadRapport = ({ folder, onUpdate, prefix }) => {
  const [files, setFiles] = useState([]);
  const [isHovered, setIsHovered] = useState(false); // To track hover state
  const [loading, setLoading] = useState(false);

  // Function to handle file drop and upload
  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      try {
        setLoading(true);
        const auth = getAuthCentral();
        const currentUser = auth.currentUser;
        const token = await getOrRefreshFirebaseToken();

        if (!currentUser) {
          console.error("User not authenticated");
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (!userSnapshot.exists()) {
          console.error("User document not found");
          return;
        }

        const org_id = userSnapshot.get("org_id");
        let repo_name = await translateDocumentId("rapport");

        await addActivityLog(
          "Documents déposés " + repo_name,
          folder.ref,
          org_id,
          userRef.id,
          "file_drop",
          prefix
        );

        // Append new files to the existing list
        const newFiles = [...files, ...acceptedFiles];
        setFiles(newFiles);

        for (const file of acceptedFiles) {
          const fileBytes = await file.arrayBuffer(); // Read file as bytes

          const url = prefix + `folders/${folder.id}/rapport`;
          const formData = new FormData();
          formData.append("file", new Blob([fileBytes]), file.name);

          // Set request headers
          const headers = {
            Authorization: `Bearer ${token}`,
          };
          let start = new Date();

          // Send the request
          const response = await fetch(url, {
            method: "POST",
            headers: headers,
            body: formData,
          });

          // Read the response body only once
          const responseText = await response.text();
          let end = new Date();
          let elapsed = end - start;
          // Add log document
          await addLogDocument(
            url,
            prefix,
            userRef,
            org_id,
            response.status.toString(),
            folder.id || "",
            "", // Assuming empty for docId
            "", // request body as string
            responseText,
            "POST",
            elapsed
          );

          setLoading(false);

          // Check response status
          if (response.ok) {
          } else {
            console.error(
              `Error response for file ${file.name}: ${response.status}`
            );
          }
        }

        // Trigger the onUpdate callback after all files are uploaded
        onUpdate();
      } catch (error) {
        console.error("Error uploading files:", error);
      }
    },
    [files, folder, onUpdate]
  );

  // Handle deletion of files
  const handleDelete = (fileToDelete) => {
    const updatedFiles = files.filter((file) => file !== fileToDelete);
    setFiles(updatedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    accept: {
      "application/pdf": [], // PDF files
      "application/xml": [], // XML files
      "application/zip": [], // ZIP files
    },
    maxFiles: Infinity,
    maxSize: 52428800,
    multiple: true, // Allow multiple file uploads
  });

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Box
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)} // Trigger hover effect
        onMouseLeave={() => setIsHovered(false)} // Remove hover effect
        sx={{
          border: "1px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: "8px",
          height: "30px",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          padding: "0px 0px",
          width: "30px",
          cursor: "pointer",
          backgroundColor: loading
            ? "rgba(0, 0, 0, 0.1)"
            : isDragActive
              ? "rgba(0, 0, 255, 0.1)"
              : isHovered
                ? "rgba(0, 0, 0, 0.03)" // When hovered
                : "transparent", // Default background
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="text-h3 text-shadow_50">
            <SmallSpinner loading={true}></SmallSpinner>
          </div>
        ) : isDragActive ? (
          <Icon1 icon_name="join" size={15} fill="grey" />
        ) : (
          <Icon1 icon_name="join" size={15} fill="grey" />
        )}
      </Box>
    </div>
  );
};

export default UploadRapport;
